module Meevo.Plugins.NotificationTemplates {
    "use strict";

    import CommunicationTypeEnum = Meevo.Model.CommunicationTypeEnum;

    /**
    * Main Section of SecurityRole 
    */
    export class MainSection extends Meevo.Components.BaseClasses.DataMaintenanceSectionBase<Meevo.Model.Template> {
        /**
        * Base class override which defines what a DEFAULT model looks like.
        */
        public missingField: any = -1;
        public communicationUserType: any;

        /**
        * Services that use this template as their notification override. Loaded on demand rather than as part
        * of the template: the notification send path reads templates by id and must not pay for the lookup,
        * and the cached template item is not invalidated when a service's override changes. Accumulates as
        * the user scrolls - see loadMoreServices - rather than being replaced a page at a time.
        */
        public servicesUsingTemplate: Meevo.Model.DataDetail[] = [];

        /**
        * Services are fetched a page at a time on scroll. No count endpoint backs this: whether there is
        * another page is inferred from the last page fetched coming back full (see loadMoreServices).
        */
        private static readonly SERVICES_PER_PAGE = 10;

        /**
        * Id of the scrollable list element in main.html, used to bind the scroll listener via jQuery.
        */
        private static readonly SERVICES_LIST_ID = "template-services-scroll-list";

        private servicesNextPage: number = 0;
        public servicesLoading: boolean = false;

        /**
        * Whether another page might exist. Starts true so the first open always fetches; cleared once a
        * fetch returns fewer than a full page.
        */
        public servicesHasMore: boolean = true;

        /**
        * Bound to the popup's own visible state (see meevo-popup-tooltip). Closed here on selecting a
        * service, before navigating to it - otherwise the popup is still open, showing the pre-change list,
        * when the user comes back. Reopening always re-fetches (onServicesPanelOpened), so it can never
        * show what the popup already had cached from before the user went and changed something.
        */
        public servicesPanelOpen: boolean = false;

        /**
        * Set by the context right after construction (initializeSections() runs before this section exists
        * as a field on it, so it cannot ask isApplicationModeCO()/isSingleLocation() itself). The
        * service-to-template link is tenant scoped, so the usage message and list only make sense from a
        * tenant-wide vantage point: Central Office, or a single-location tenant where the one location is
        * that vantage point (there is no separate CO to switch to, so isApplicationModeCO() alone is
        * false there even though the same tenant-wide view applies).
        */
        public servicesUsageAllowed: boolean = false;

        /**
        * The notification types a service can override, per the set ServiceLogic.GetNotificationOverrideAsync
        * builds for the service's own Notifications tab. Client only: the same types also exist for Employee
        * (different Type_SId) but the service screen never offers those, so a link written against one would
        * be invisible there and unmanageable. Due in for Service Reminder is left out on purpose - unlike
        * Post Service Care it was not called out as in scope.
        */
        private static isSupportedForServiceOverride(model: any): boolean {
            if (!model) {
                return false;
            }

            const supported = [
                CommunicationTypeEnum.AppointmentReminderOnAdd,
                CommunicationTypeEnum.AppointmentReminderOnCancel,
                CommunicationTypeEnum.AppointmentReminderOnUpdate,
                CommunicationTypeEnum.PostServiceCare
            ];

            return model.communicationTypeUser === Meevo.Model.CommunicationTypeUserEnum.Client
                && supported.indexOf(model.communicationType) !== -1;
        }

        createSectionModelInstance(postAdd: (data: any) => any): void {
            let instance: Model.Template = {
                id: Guid.empty.toString(),
                objectState: Meevo.Model.ObjectStateEnum.Active,
                objectCreationType: Meevo.Model.ObjectCreationTypeEnum.User
            };

            this.model = instance;
            this.resetServicesUsingTemplate();
        }

        private resetServicesUsingTemplate(): void {
            this.servicesUsingTemplate = [];
            this.servicesNextPage = 0;
            this.servicesHasMore = true;
            this.servicesLoading = false;
        }

        /**
        * Whether the usage message and its services list apply at all: Central Office (or a single-location
        * tenant, see servicesUsageAllowed) only, and only for the notification types a service can override.
        * Static per template, so the message can be gated directly on it - unlike a count, nothing here
        * needs a network round trip to know.
        */
        get showServicesUsage(): boolean {
            return !!this.model && !!this.model.isServiceDefault && this.servicesUsageAllowed
                && MainSection.isSupportedForServiceOverride(this.model);
        }

        sectionModelRetrieved(): void {
            super.sectionModelRetrieved();
            this.resetServicesUsingTemplate();
            this.bindServicesScrollListener();
            this.refreshServicesIfPanelStillOpen();
        }

        /**
        * The popup is deliberately left open when the user follows a service out of it (see goToService),
        * so coming back here can land on an already-open panel rather than a freshly closed one. Whether
        * that happens re-fetches this section from scratch or reuses it depends on navigation internals
        * this class has no visibility into, so cover both: if the panel is already open, refresh it now
        * exactly as if it had just been opened, rather than trusting whatever it showed before the user
        * went and possibly changed the very thing it lists.
        */
        private refreshServicesIfPanelStillOpen(): void {
            if (this.servicesPanelOpen) {
                this.loadMoreServices();
            }
        }

        /**
        * Binds the scroll-to-bottom listener once per template load. Safe to do unconditionally: the list
        * stays in the DOM even while the usage message is hidden (main.html uses ng-show there, precisely
        * so this element exists from the start), and AngularJS has no built-in scroll binding, so this
        * follows the same jQuery approach already used elsewhere for scroll-driven paging (see
        * typeAhead.ts) and for DOM timing in this same class (see beforeSetFocusOnField).
        */
        private bindServicesScrollListener(): void {
            setTimeout(() => {
                let list = $('#' + MainSection.SERVICES_LIST_ID);
                list.off('scroll').on('scroll', () => {
                    let el = list[0];
                    if (el && el.scrollHeight - Math.ceil(el.scrollTop) <= el.clientHeight) {
                        this.loadMoreServices();
                    }
                });
            }, 0);
        }

        /**
        * Opens the popup: starts the list over rather than trusting whatever a previous open cached - a
        * service picked from that previous open may have just been detached, and nothing else here
        * invalidates the cache when the user comes back to it.
        */
        onServicesPanelOpened(): void {
            this.resetServicesUsingTemplate();
            this.loadMoreServices();
        }

        /**
        * Fetches and appends the next page. Used both for the initial page on open and for every
        * subsequent page as the user scrolls the list. There is no count endpoint behind this: a page
        * coming back short of a full page is what marks the end.
        */
        loadMoreServices(): void {
            if (this.servicesLoading || !this.servicesHasMore || !this.model || !this.model.id) {
                return;
            }

            this.servicesLoading = true;

            let request = {
                pageNumber: this.servicesNextPage,
                itemsPerPage: MainSection.SERVICES_PER_PAGE
            };

            this.dataService.getListPaged(this.baseEndpoint, request as any, this.model.id as any, 'Services')
                .then((page: Meevo.Model.DataDetail[]) => {
                    page = page || [];
                    this.servicesUsingTemplate = this.servicesUsingTemplate.concat(page);
                    this.servicesHasMore = page.length === MainSection.SERVICES_PER_PAGE;
                    this.servicesNextPage++;
                })
                .catch(() => {
                    // Stop rather than retry into a failing request; whatever loaded already stays shown.
                    this.servicesHasMore = false;
                })
                .finally(() => {
                    this.servicesLoading = false;
                    this.fillServicesListIfNeeded();
                });
        }

        /**
        * A scroll listener only fires once there is something to scroll. With a small enough total, a page
        * of 10 can fill (or undershoot) the popup's max-height without ever producing a scrollbar, which
        * would silently strand servicesHasMore at true with no way for the user to trigger the next page.
        * So after every page, if there is still no overflow and more might exist, fetch the next page too.
        */
        private fillServicesListIfNeeded(): void {
            if (!this.servicesHasMore) {
                return;
            }

            setTimeout(() => {
                let el = document.getElementById(MainSection.SERVICES_LIST_ID);
                if (el && el.scrollHeight <= el.clientHeight) {
                    this.loadMoreServices();
                }
            }, 0);
        }

        beforeSetFocusOnField() {
            var that = this;

            setTimeout(function () { that.setFocusOnField(that.missingField) }, 400);
        }

        setFocusOnField(field: any) {
            switch (field) {
                default:
                    $("[ng-required='required']:visible:first").focus();
                    break;
            }
        }

        preSave() {
            if (this.model.communicationType === CommunicationTypeEnum.GenericOptInRequested) {
                this.model.emailSubject = '';
                this.model.emailBody = '';
            }

            if (typeof this.model.smsMessage !== 'undefined' && this.model.smsMessage !== "") {
                let parser = new DOMParser();
                let parsedSMSMessage = parser.parseFromString(this.model.smsMessage, 'text/html');
                this.model.smsMessage = parsedSMSMessage.body.textContent;
            }

            if (typeof this.model.emailSubject !== 'undefined' && this.model.emailSubject !== "") {
                let parser = new DOMParser();
                let parsedEmailSubject = parser.parseFromString(this.model.emailSubject, 'text/html');
                this.model.emailSubject = parsedEmailSubject.body.textContent;
            }
        }

        get communicationTypeEnum(): any {
            return CommunicationTypeEnum;
        }

        // sets the smsMessage field to System level GenericOptInRequested SMSMessage.
        setSMSMessage(commType: any, dsCommType: any) {
            if (commType === CommunicationTypeEnum.GenericOptInRequested && dsCommType != null) {
                if (!this.model.smsMessage) {
                    let optInReqObj = dsCommType.filter(d => d.communicationType === commType
                        && d.communicationTypeUser === Meevo.Model.CommunicationTypeUserEnum.Client)
                    this.model.smsMessage = optInReqObj[0].smsMessage;
                }
            }
                        
        }

        
        

        GetCommTypeFilteredList(userType: any, dsCommType: any) {
            if (dsCommType != null && userType != 'undefined' && userType != "") {
                return dsCommType.filter(d=> d.communicationTypeUser == userType);
            }
        }
        
    }

}